import { cellCode, isoAgo, lpCode, seeded, skuArticle, skuName, SKU_COUNT, WORKERS } from "./fixtures";

/**
 * Modul ro'yxatlari uchun namuna yozuvlar.
 *
 * Har bir ro'yxat AJRALIB TURADIGAN holatlar bilan to'ldirilgan:
 * ochiq va yopiq blok, tasdiqlangan va kutayotgan sanash,
 * yetkazilgan va dead-letter xabar. Hammasi bir xil bo'lsa,
 * interfeysning holat mantiqi sinalmay qolardi.
 */

const BLOCK_REASONS = [
  "INVENTORY", "DAMAGE", "CUSTOMS", "DATA_ERROR",
  "RESERVATION_CONFLICT", "LOCATION_MISMATCH", "MANUAL",
];

export function locationBlocks() {
  const random = seeded(11);
  return Array.from({ length: 24 }, (_, index) => {
    const active = index % 3 !== 0;
    return {
      id: `blk-${1000 + index}`,
      location_id: `loc-${index}`,
      location_code: cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
      reason: BLOCK_REASONS[index % BLOCK_REASONS.length]!,
      scope: index % 4 === 0 ? "SKU" : "ALL",
      status: active ? "ACTIVE" : "CLOSED",
      source_event: index % 3 === 0 ? "control.shortage.v1" : "",
      note: index % 5 === 0 ? "Nazoratda kamomad aniqlandi" : "",
      opened_at: isoAgo(Math.round(random() * 5000) + 60),
      closed_at: active ? undefined : isoAgo(Math.round(random() * 500)),
      close_reason: active ? undefined : "Inventarizatsiya tasdiqlandi",
      version: (index % 3) + 1,
    };
  });
}

export function inventoryCounts() {
  const random = seeded(23);
  // Server holatlari (inventory-count-service): sanalmoqda, tasdiq
  // kutilmoqda (REVIEW), tasdiqlangan.
  const statuses = ["COUNTING", "REVIEW", "APPROVED", "REVIEW", "COMPLETED"];
  const processes = ["PICKING", "CONTROL", "REPLENISHMENT", "MANUAL"];

  return Array.from({ length: 18 }, (_, index) => {
    const status = statuses[index % statuses.length]!;
    return {
      id: `cnt-${2000 + index}`,
      count_number: `INV-${String(4100 + index).padStart(6, "0")}`,
      count_type: index % 5 === 4 ? "POINT" : "DISCREPANCY",
      reason_process: processes[index % processes.length]!,
      warehouse_code: "A",
      freeze_mode: index % 3 === 0 ? "RECONCILE" : "BLOCK",
      status,
      require_separate_approver: true,
      counted_by: status === "COUNTING" ? undefined : WORKERS[index % WORKERS.length]!,
      approved_by: status === "APPROVED" || status === "COMPLETED" ? "Dispetcher" : undefined,
      created_at: isoAgo(Math.round(random() * 4000) + 120),
      version: (index % 4) + 1,
    };
  });
}

/** Dispetcher taxtasi: ijrochilar, rollar, joriy ish. */
const EXECUTOR_ROLES = ["picker", "forklift_driver", "receiver", "controller", "inventory_counter", "shipper"];
const ROLE_TASK: Record<string, string> = {
  picker: "PICK", forklift_driver: "PUTAWAY", receiver: "RECEIPT", controller: "CONTROL",
  inventory_counter: "COUNT", shipper: "SHIPMENT",
};

export function dispatchStaff() {
  return WORKERS.map((name, index) => {
    const role = EXECUTOR_ROLES[index % EXECUTOR_ROLES.length]!;
    const busy = index % 3 !== 2;
    return {
      user_id: `usr-${index}`,
      username: name.toLowerCase().replace(/[^a-z]/g, "").slice(0, 10),
      full_name: name,
      roles: index === 0 ? [role, "senior_staff"] : [role],
      busy,
      assigned: busy ? 1 : 0,
      current: busy
        ? { id: `tsk-cur-${index}`, task_type: ROLE_TASK[role]!, status: index % 2 ? "IN_PROGRESS" : "RESERVED",
            priority: 30, required_role: role, created_at: isoAgo(25 + index * 7) }
        : null,
    };
  });
}

export function dispatchTasks() {
  // Yigirma to'rtta: navbat ekranida har bir turda bir nechta
  // qator bo'lishi kerak — bitta qatorni sudrab bo'lmaydi.
  return Array.from({ length: 24 }, (_, index) => {
    const role = EXECUTOR_ROLES[index % EXECUTOR_ROLES.length]!;
    return {
      id: `tsk-${7000 + index}`,
      task_type: ROLE_TASK[role]!,
      status: ROLE_TASK[role] === "PICK" ? "RESERVED" : "CREATED",
      priority: 10 + index * 5,
      required_role: role,
      warehouse_code: "A",
      // Manba jarayon TUR bilan bir xil emas: nazorat topshirig'i
      // terishdan, joylashtirish esa qabuldan tug'iladi. Ularni
      // bog'lab qo'yish navbat ekranida har bir qatorni bir xil
      // so'z bilan ko'rsatardi.
      source_process: ["PICKING", "INBOUND", "PICKING", "SHIPPING", "INBOUND"][index % 5],
      // Hujjatdan tug'ilgan topshiriqda hujjat IDsi bo'ladi —
      // «Terishga chiqarish» oynasi xodimga aynan shular bo'yicha
      // biriktiradi. Har uchinchisi hujjatsiz: to'ldirish va
      // inventarizatsiya buyurtmadan kelmaydi.
      order_id: (index % 3 === 2
        ? undefined
        : `ord-${String(4100 + index)}`) as string | undefined,
      // Navbat ekrani qayta rejalashtirishda versiyani yuboradi.
      version: 1,
      created_at: isoAgo(5 + index * 11),
      lines: [{ sku_id: `sku-${index}`, planned_qty: String(6 + index) }],
    };
  });
}

const CHANNELS = ["IN_APP", "TELEGRAM", "IN_APP", "EMAIL"];
const NOTIFICATION_STATUSES = ["SENT", "SENT", "PENDING", "DEAD", "SENT"];

export function notifications() {
  const random = seeded(37);
  const events = [
    "Yacheyka bloklandi",
    "Kamomad aniqlandi",
    "Menejer qarori kutilmoqda",
    "Sariq sektorda kechikish",
    "To'ldirish talab qilinadi",
    "Inventarizatsiya yakunlandi",
  ];

  return Array.from({ length: 32 }, (_, index) => {
    const status = NOTIFICATION_STATUSES[index % NOTIFICATION_STATUSES.length]!;
    return {
      id: `ntf-${3000 + index}`,
      rule_code: `NTF-${String((index % 6) + 1).padStart(3, "0")}`,
      subject: events[index % events.length]!,
      channel: CHANNELS[index % CHANNELS.length]!,
      recipient: index % 3 === 0 ? "dispetcher" : WORKERS[index % WORKERS.length]!,
      status,
      attempts: status === "DEAD" ? 5 : status === "PENDING" ? 1 : 1,
      last_error: status === "DEAD" ? "Telegram javob bermadi (timeout)" : "",
      created_at: isoAgo(Math.round(random() * 2000) + 5),
      sent_at: status === "SENT" ? isoAgo(Math.round(random() * 1800)) : undefined,
    };
  });
}

export function notificationRules() {
  // Haqiqiy API shakli (notification-service domain.AllRules):
  // kod, rol, ustuvorlik, tavsif va roldagi faol xodimlar soni.
  return [
    { code: "LP_MISMATCH", role: "senior_staff", priority: "HIGH",
      description: "Joylashtirishda LP mos kelmadi", recipient_count: 2 },
    { code: "ORDER_INCOMPLETE", role: "order_manager", priority: "HIGH",
      description: "Buyurtma to'liq terilmadi (FIX-05)", recipient_count: 0 },
    { code: "WAITING_MANAGER", role: "order_manager", priority: "CRITICAL",
      description: "Nazorat kamomadi menejer qarorini kutmoqda", recipient_count: 0 },
    { code: "ZONE_DELAY", role: "dispatcher", priority: "NORMAL",
      description: "Sariq hududda pallet kechikdi", recipient_count: 1 },
    { code: "INTEGRATION_ERROR", role: "administrator", priority: "CRITICAL",
      description: "1C almashinuvida xato", recipient_count: 1 },
  ];
}

const AUDIT_ACTIONS = [
  { action: "location.blocked", entity: "location", label: "Yacheyka bloklandi" },
  { action: "task.completed", entity: "task", label: "Topshiriq yakunlandi" },
  { action: "config.activated", entity: "config", label: "Sozlama faollashtirildi" },
  { action: "count.approved", entity: "inventory_count", label: "Sanash tasdiqlandi" },
  { action: "user.login", entity: "user", label: "Tizimga kirish" },
  { action: "location.unblocked", entity: "location", label: "Blok yechildi" },
];

export function auditEvents() {
  const random = seeded(43);

  return Array.from({ length: 60 }, (_, index) => {
    const entry = AUDIT_ACTIONS[index % AUDIT_ACTIONS.length]!;
    return {
      id: `aud-${5000 + index}`,
      occurred_at: isoAgo(Math.round(random() * 6000) + index),
      actor_name: index % 4 === 0 ? "Dispetcher" : WORKERS[index % WORKERS.length]!,
      actor_role: index % 4 === 0 ? "dispatcher" : "picker",
      action: entry.action,
      entity_type: entry.entity,
      entity_id: `${entry.entity}-${100 + index}`,
      summary: entry.label,
      reason: index % 3 === 0 ? "Nazoratda tafovut aniqlandi" : "",
      warehouse_code: "A",
      payload_json: {
        location_code: cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
        version: (index % 5) + 1,
      },
      before_state: index % 2 === 0 ? { status: "ACTIVE" } : undefined,
      after_state: index % 2 === 0 ? { status: "CLOSED" } : undefined,
    };
  });
}

/** Rol bo'yicha vakolatlar — tafsilot panelida ochiladi. */
const PERMISSIONS: Record<string, string[]> = {
  administrator: [
    "user.read", "user.write", "role.manage", "device.manage",
    "config.read", "config.edit", "config.activate", "audit.read",
    "report.read", "report.export", "topology.read", "topology.write",
    "inventory.read", "task.read", "count.read", "notification.read",
  ],
  dispatcher: [
    "task.read", "task.assign", "task.replan", "inventory.read",
    "inventory.block.create", "report.read", "count.read", "notification.read",
    "picking.read",
  ],
  warehouse_manager: [
    "report.read", "report.export", "config.read", "config.edit",
    "inventory.read", "topology.read", "count.approve",
  ],
  controller: ["control.read", "control.execute", "report.read", "inventory.read"],
  picker: ["task.read", "task.execute", "picking.read", "putaway.read"],
  receiver: ["task.read", "task.execute", "inbound.read"],
  forklift_driver: ["task.read", "task.execute", "putaway.read"],
  senior_staff: ["task.read", "task.execute", "inventory.read", "picking.read"],
  inventory_counter: ["count.read", "count.perform", "inventory.read"],
};

/**
 * Namuna xodimlari.
 *
 * Rollar ISMGA mos: `WORKERS` — ombor xodimlari va ularni
 * administrator qilib qo'yish boshqaruv panelining «bo'sh xodim»
 * sonini nolga tushirardi (u faqat topshiriq bajaradigan rollarni
 * sanaydi). Oxirgi uchtasi — ofis rollari.
 */
export function users() {
  const roleCodes = [
    "picker", "receiver", "forklift_driver", "picker", "controller", "senior_staff",
    "administrator", "dispatcher", "warehouse_manager",
  ];

  return WORKERS.concat(["Bootstrap administrator", "Dispetcher D.", "Ombor rahbari R."]).map(
    (name, index) => {
      const role = roleCodes[index]!;
      return {
        id: `usr-${index}`,
        username:
          name.toLowerCase().replace(/[^a-z]/g, "").slice(0, 10) || `user${index}`,
        full_name: name,
        roles: [role],
        permissions: PERMISSIONS[role] ?? [],
        warehouses: ["A"],
        is_active: index !== 5,
        version: 1,
        created_at: isoAgo(index * 1440 + 4320),
        updated_at: isoAgo(index * 37 + 5),
      };
    },
  );
}

export function roles() {
  const catalog = [
    { code: "administrator", label: "Administrator", note: "To'liq huquq" },
    { code: "dispatcher", label: "Dispetcher", note: "Topshiriq taqsimlash va qayta rejalash" },
    { code: "warehouse_manager", label: "Ombor rahbari", note: "Sozlama va hisobotlar" },
    { code: "controller", label: "Nazoratchi", note: "Nazorat stoli" },
    { code: "picker", label: "Teruvchi", note: "Terish topshirig'i" },
    { code: "inventory_counter", label: "Inventarizator", note: "Sanash" },
  ];

  return catalog.map((role, index) => ({
    id: `rol-${index}`,
    code: role.code,
    name: role.label,
    description: role.note,
    permissions: PERMISSIONS[role.code] ?? [],
    is_system: index < 2,
  }));
}

export function devices() {
  const random = seeded(61);

  return Array.from({ length: 9 }, (_, index) => ({
    id: `dev-${index}`,
    device_code: `TSD-${String(index + 1).padStart(3, "0")}`,
    model: index % 3 === 0 ? "Zebra MC3300" : "Honeywell EDA52",
    warehouse_code: "A",
    assigned_user_id: index < WORKERS.length ? `usr-${index}` : undefined,
    status: index === 7 ? "REVOKED" : "ACTIVE",
    last_seen_at: isoAgo(Math.round(random() * 600)),
    created_at: isoAgo(index * 2880 + 7200),
    version: 1,
  }));
}

// --- Menejer qarorlari ---

/**
 * Guruh: 1C KODI va NOMI.
 *
 * Namunada ikkalasi ham bor, chunki kartaning butun gapi shu —
 * ro'yxatda nom ko'rinadi, kod esa 1C bilan solishtirish uchun
 * yonida qoladi. Har to'rtinchi tovarda nom YO'Q: 1C eski
 * versiyasi uni yubormaydi va interfeys bunda kodga tushishi
 * sinalishi kerak.
 */
const SKU_GROUPS = [
  { code: "GD-000431", name: "Oshxona idishlari" },
  { code: "GD-000469", name: "Choy to'plamlari" },
  { code: "GD-000512", name: "Laganlar" },
];

/** Brend 1C dan keladi; hammasida ham bo'lmaydi. */
const SKU_BRANDS = ["PASABAHCE", "LUMINARC", "НЕМАН", ""];

export function skus() {
  const random = seeded(83);
  return Array.from({ length: SKU_COUNT }, (_, index) => {
    const name = skuName(index);
    // Har beshinchi tovarda VGH yo'q — «VGH yo'q» filtri bo'sh qolmasin.
    const complete = index % 5 !== 2;
    const ean = String(4780000000000 + index * 1013 + Math.round(random() * 900));
    return {
      id: `sku-${index}`,
      article: skuArticle(index),
      name,
      name_ru: undefined as string | undefined,
      // Har beshinchisi komplekt: «dona» va «komplekt» bir xil
      // tovar emas va qabulda miqdor aynan shu birlikda
      // sanaladi.
      base_uom: index % 5 === 4 ? "komplekt" : "dona",
      product_group: SKU_GROUPS[index % SKU_GROUPS.length]!.code,
      product_group_name: index % 4 === 3 ? undefined : SKU_GROUPS[index % SKU_GROUPS.length]!.name,
      brand: SKU_BRANDS[index % SKU_BRANDS.length] || undefined,
      // Rasm 1C dan havola sifatida keladi (fayl emas). Har
      // uchinchisida yo'q — «rasm yo'q» holati ham sinalsin.
      photo_url: index % 3 === 0 ? undefined : skuPhoto(index),
      abc_class: ["A", "B", "C"][index % 3],
      xyz_class: ["X", "Y", "Z"][(index + 1) % 3],
      dimensions: complete
        ? { length_cm: String(12 + (index % 7) * 4), width_cm: String(10 + (index % 5) * 3),
            height_cm: String(6 + (index % 4) * 5), weight_kg: (0.2 + (index % 6) * 0.15).toFixed(3) }
        : {},
      has_complete_vgh: complete,
      ikpu_code: complete ? `069110010010${String(index).padStart(5, "0")}` : undefined,
      tnved_code: "6911100000",
      fragile: index % 4 === 0,
      no_stack: index % 9 === 0,
      // Hisob rejimi 1C niki: partiyali va seriyali tovar
      // qabulda boshqacha so'raladi va karta buni aytishi kerak.
      tracks_lot: index % 6 === 1,
      tracks_serial: index % 11 === 3,
      shelf_life_days: index % 6 === 1 ? 180 + index : undefined,
      // Shtrix-kod MASSIV: 1C da bitta tovarda bir nechta kod
      // bo'lishi odatiy hol va karta ularni ro'yxat qilib
      // ko'rsatadi.
      barcodes: index % 7 === 6 ? [] : index % 3 === 1 ? [ean, String(Number(ean) + 7)] : [ean],
      external_id: `1c-${String(index).padStart(4, "0")}-guid`,
      is_active: index !== 35,
      version: 1 + (index % 3),
      updated_at: isoAgo(index * 97 + 30),
    };
  });
}

/**
 * Namuna rasmi — SVG data-URL.
 *
 * Tashqi havola ATAYLAB emas: namuna rejimi serverga umuman
 * chiqmasligi kerak (`check:mock` buni tekshiradi) va internetsiz
 * muhitda karta singan rasm belgisi bilan ochilardi.
 */
function skuPhoto(index: number): string {
  const hue = (index * 47) % 360;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">` +
    `<rect width="120" height="120" fill="hsl(${hue} 60% 92%)"/>` +
    `<circle cx="60" cy="52" r="26" fill="hsl(${hue} 55% 70%)"/>` +
    `<rect x="34" y="82" width="52" height="10" rx="5" fill="hsl(${hue} 45% 60%)"/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function skuPackages(skuId: string) {
  return [
    { id: `${skuId}-pk0`, sku_id: skuId, code: "DONA", units_per_pack: "1", is_base: true, barcodes: [] },
    { id: `${skuId}-pk1`, sku_id: skuId, code: "KOR-12", name: "Korobka", units_per_pack: "12", barcodes: [] },
    { id: `${skuId}-pk2`, sku_id: skuId, code: "KOR-12-PALLET", name: "Pallet", units_per_pack: "480", barcodes: [] },
  ];
}

/** Kamomadi menejer qarorini kutayotgan nazorat seanslari. */
export function controlDecisions() {
  return Array.from({ length: 4 }, (_, index) => {
    const sessionId = `ctl-${500 + index}`;
    const planned = 12 + index * 6;
    const counted = planned - (index + 1) * 2;
    return {
      session: {
        id: sessionId,
        order_number: `ZK-${24180 + index * 13}`,
        status: index === 3 ? "WAITING_INVENTORY" : "WAITING_MANAGER",
        warehouse_code: "A",
        station_code: `NAZ-0${index + 1}`,
        updated_at: isoAgo(8 + index * 23),
      },
      discrepancy: {
        id: `dsc-${900 + index}`,
        session_id: sessionId,
        discrepancy_type: "SHORTAGE",
        sku_id: `sku-${index}`,
        planned_qty: String(planned),
        counted_qty: String(counted),
        remaining_qty: String(planned - counted),
        status: index === 3 ? "WAITING_INVENTORY" : "WAITING_MANAGER",
        retry_result: "NOT_FOUND",
        version: 3,
        updated_at: isoAgo(8 + index * 23),
      },
    };
  });
}

// --- 1C integratsiyasi ---

const INBOUND_EVENTS = ["nomenclature.upsert", "counterparty.upsert", "sales_order.create", "users.upsert"];
const OUTBOUND_EVENTS = ["shipping.shipment.completed", "inventory.adjusted", "return.completed"];

export interface MockMessage {
  direction: "INBOUND" | "OUTBOUND";
  message_id: string;
  event_type: string;
  entity_id: string;
  version?: number;
  status: string;
  status_code?: string;
  status_message?: string;
  status_details?: Record<string, unknown>;
  attempts?: number;
  last_http_status?: number;
  received_at: string;
  updated_at: string;
  payload?: unknown;
  onec_payload?: unknown;
  allowed_actions: string[];
}

export function integrationMessages(): MockMessage[] {
  const random = seeded(97);
  const inbound = Array.from({ length: 18 }, (_, index) => {
    const status = ["APPLIED", "APPLIED", "WAITING_MAPPING", "APPLIED", "REJECTED", "SUPERSEDED"][index % 6]!;
    return {
      direction: "INBOUND" as const,
      message_id: `msg-in-${4000 + index}`,
      event_type: INBOUND_EVENTS[index % INBOUND_EVENTS.length]!,
      entity_id: `1c-guid-${(index * 7919).toString(16).padStart(6, "0")}`,
      version: 1 + (index % 3),
      status,
      status_code: status === "WAITING_MAPPING" ? "mapping_missing" : status === "REJECTED" ? "reference_missing" : undefined,
      status_message:
        status === "WAITING_MAPPING"
          ? "1C ombori WMS korpusiga bog'lanmagan"
          : status === "REJECTED"
            ? "Buyurtma satridagi tovar nomenklaturada yo'q"
            : undefined,
      // Haqiqiy API shakli: `missing_mappings` ichida tur va kalit.
      // Ilgari bu yerda `missing_warehouses` turardi — panel esa
      // uni o'qimasdi, ya'ni namuna rejimida «qiymat kutmoqda»
      // ro'yxati hech qachon chiqmasdi.
      status_details:
        status === "WAITING_MAPPING"
          ? {
              missing_mappings: [
                index % 12 === 2
                  ? { kind: "UOM", external_key: "ящик" }
                  : { kind: "WAREHOUSE", external_key: "Склад №2" },
              ],
            }
          : undefined,
      attempts: status === "REJECTED" ? 1 : 1,
      received_at: isoAgo(index * 43 + 3),
      updated_at: isoAgo(index * 41 + 2),
      payload: { id: `1c-guid-${index}`, version: 1 + (index % 3), lines: [{ article: `UY-${1040 + index}`, qty: Math.round(random() * 40) }] },
      allowed_actions: ["WAITING_MAPPING", "REJECTED", "FAILED"].includes(status) ? ["retry"] : [],
    };
  });
  const outbound = Array.from({ length: 14 }, (_, index) => {
    const status = ["DELIVERED", "PENDING", "DELIVERED", "DEAD_LETTER", "WAITING_MAPPING", "MANUAL_REVIEW", "DELIVERED"][index % 7]!;
    return {
      direction: "OUTBOUND" as const,
      message_id: `msg-out-${5000 + index}`,
      event_type: OUTBOUND_EVENTS[index % OUTBOUND_EVENTS.length]!,
      entity_id: `wms-${(index * 104729).toString(16).padStart(8, "0")}`,
      status,
      status_code: status === "DEAD_LETTER" ? "delivery_failed" : undefined,
      status_message: status === "DEAD_LETTER" ? "1C 5 marta 503 qaytardi" : undefined,
      attempts: status === "DEAD_LETTER" ? 5 : status === "PENDING" ? 2 : 1,
      last_http_status: status === "DEAD_LETTER" ? 503 : status === "DELIVERED" ? 200 : undefined,
      received_at: isoAgo(index * 37 + 6),
      updated_at: isoAgo(index * 29 + 4),
      onec_payload: { document: `WMS-${7000 + index}`, lines: 3 },
      allowed_actions: ["MANUAL_REVIEW", "DEAD_LETTER", "WAITING_MAPPING"].includes(status) ? ["retry"] : [],
    };
  });
  return [...inbound, ...outbound];
}

export function integrationMappings() {
  return [
    { kind: "WAREHOUSE", external_key: "Основной склад", wms_value: "A", is_active: true, note: "Asosiy korpus" },
    { kind: "WAREHOUSE", external_key: "Склад паллетный", wms_value: "B", is_active: true },
    { kind: "WAREHOUSE", external_key: "Склад №3", wms_value: "C", is_active: true },
    { kind: "UOM", external_key: "шт", wms_value: "dona", is_active: true },
    { kind: "UOM", external_key: "кор", wms_value: "KOR", is_active: true },
    { kind: "ROLE", external_key: "Кладовщик", wms_value: "picker", is_active: true },
    { kind: "ROLE", external_key: "Водитель погрузчика", wms_value: "forklift_driver", is_active: true },
  ].map((row, index) => ({ ...row, version: 1, updated_at: isoAgo(index * 1440 + 90) }));
}

/**
 * Serverdan tashqari zaxira sozlamasi.
 *
 * Bot SOZLANGAN, lekin oxirgi yuborish XATO bilan tugagan: panel
 * «sozlangan» va «ishlamadi» ni bir vaqtda ko'rsata olishi kerak,
 * aks holda nosozlik faqat serverdagi logda qolardi.
 */
export function offsite() {
  return {
    bot_token_mask: "\u2022\u2022\u2022\u2022kQ7f",
    bot_token_set: true,
    api_hash_mask: "\u2022\u2022\u2022\u20229a1c",
    api_hash_set: true,
    chat_id: "-1001234567890",
    api_id: "2040",
    key_recipient_id: "77001122",
    configured: true,
    bot_api_url: "http://telegram-bot-api:8081",
    key_available: true,
    encrypted: true,
    updated_at: isoAgo(2880),
    version: 3,
    last_run: {
      status: "FAILED",
      detail: "Telegram javob bermadi (timeout)",
      chunk_count: 2,
      total_bytes: 3_120_000_000,
      generation: "2026-10-10",
      started_at: isoAgo(95),
      finished_at: isoAgo(88),
    },
  };
}

/** 1C Basic hisobi — saqlangan, parol niqoblangan. */
export function onecAuth() {
  return {
    username: "WMS Integratsiya",
    password_mask: "\u2022\u2022\u2022\u2022r3t@",
    password_set: true,
    configured: true,
    encrypted: true,
    from_env: false,
    updated_at: isoAgo(1440),
    version: 2,
  };
}

/** LP holatlari — hayot yo'lining har bir bosqichi ko'rinsin. */
const LP_STATUSES = ["MX", "OX", "RECEIVING", "WITH_EMPLOYEE", "EMPTY", "CLOSED"];

/**
 * Palletlar va tara.
 *
 * Har bir holat namunada BOR: panel filtri bo'sh ro'yxat
 * qaytarsa, u ishlayaptimi yoki shunchaki ma'lumot yo'qmi —
 * bilib bo'lmasdi.
 */
export function licensePlates() {
  const random = seeded(47);
  return Array.from({ length: 28 }, (_, index) => {
    const status = LP_STATUSES[index % LP_STATUSES.length]!;
    const placed = status === "MX" || status === "OX";
    // Bo'sh va yopilgan LP da tarkib BO'LMAYDI — aks holda
    // «bo'sh» degan holat o'z nomini yo'qotardi.
    const lines = status === "EMPTY" || status === "CLOSED" ? 0 : (index % 3) + 1;
    return {
      id: `lp-${index}`,
      lp_code: lpCode(index + 120),
      lp_type: index % 4 === 1 ? "CONTAINER" : index % 7 === 3 ? "BOX" : "PALLET",
      status,
      current_location_id: placed ? `loc-${index % 24}` : undefined,
      current_holder_id: status === "WITH_EMPLOYEE" ? `usr-${index % 6}` : undefined,
      warehouse_code: "A",
      weight_kg: placed ? String(120 + (index % 9) * 15) : undefined,
      length_cm: placed ? "120" : undefined,
      width_cm: placed ? "80" : undefined,
      height_cm: placed ? String(90 + (index % 5) * 10) : undefined,
      contents: Array.from({ length: lines }, (_, line) => ({
        sku_id: `sku-${(index * 3 + line) % SKU_COUNT}`,
        lot_ref: line === 0 && index % 4 === 0 ? `L-${2000 + index}` : undefined,
        qty: String(Math.round(random() * 40) + 6),
      })),
      has_stock: lines > 0,
      closed_at: status === "CLOSED" ? isoAgo(Math.round(random() * 400) + 60) : undefined,
      version: (index % 4) + 1,
      created_at: isoAgo(Math.round(random() * 9000) + 400),
      updated_at: isoAgo(Math.round(random() * 400) + 5),
    };
  });
}

/** LP harakatlari — nima qo'yilgan va nima olingan. */
export function lpMovements(lpId: string) {
  const index = Number(lpId.replace("lp-", "")) || 0;
  const random = seeded(index + 300);
  const types = ["RECEIPT", "PUTAWAY", "PICK", "MOVE"];
  return Array.from({ length: (index % 4) + 2 }, (_, step) => ({
    id: `mv-${lpId}-${step}`,
    movement_type: types[(index + step) % types.length]!,
    sku_id: `sku-${(index * 3 + step) % SKU_COUNT}`,
    qty: String(Math.round(random() * 30) + 4),
    source_location_id: step === 0 ? undefined : `loc-${(index + step) % 24}`,
    target_location_id: `loc-${(index + step + 1) % 24}`,
    reason: step === 0 ? "Qabul" : "",
    occurred_at: isoAgo(Math.round(random() * 2000) + step * 120 + 10),
  }));
}

/** Manzillar — LP va harakatlarda `location_id` ni kodga aylantirish uchun. */
export function locations() {
  return Array.from({ length: 24 }, (_, index) => ({
    id: `loc-${index}`,
    code: cellCode((index % 40) + 1, (index % 9) + 1, (index % 29) + 1),
  }));
}

/**
 * Topshiriqlar tarixi: yakunlangan va bekor qilinganlar.
 *
 * Har xil holat ATAYLAB: to'liq bajarilgan, kamomad bilan yopilgan,
 * boshlanmasdan bekor qilingan va hujjatsiz (qisqa ID bilan
 * ko'rinadigan) topshiriq — tarix ekranining har bir belgisi
 * namunada ham sinalsin.
 */
const HISTORY_TYPES = [
  "PICK", "PUTAWAY", "RECEIPT", "CONTROL", "PALLET_LOWER", "PALLET_LIFT", "COUNT", "REPLENISHMENT",
];
const HISTORY_ROLE: Record<string, string> = {
  PICK: "picker", PUTAWAY: "forklift_driver", RECEIPT: "receiver", CONTROL: "controller",
  PALLET_LOWER: "forklift_driver", PALLET_LIFT: "forklift_driver", COUNT: "inventory_counter",
  REPLENISHMENT: "forklift_driver",
};

export function finishedTasks() {
  const random = seeded(97);
  return Array.from({ length: 64 }, (_, index) => {
    const taskType = HISTORY_TYPES[index % HISTORY_TYPES.length]!;
    const cancelled = index % 9 === 4;
    // Bekor qilinganlarning yarmi hech kim olmasdan yopilgan.
    const assignee = cancelled && index % 2 === 0 ? undefined : `usr-${index % WORKERS.length}`;
    const finishedAgo = index * 47 + Math.round(random() * 40) + 6;
    const workMinutes = Math.round(random() * 50) + 4;
    const createdAgo = finishedAgo + workMinutes + Math.round(random() * 90) + 5;
    const started = !cancelled || assignee !== undefined;

    const lines = Array.from({ length: (index % 5) + 1 }, (_, line) => {
      const planned = Math.round(random() * 40) + 2;
      const short = !cancelled && index % 6 === 1 && line === 0;
      const actual = cancelled
        ? undefined
        : short
          ? Math.max(planned - Math.round(random() * 3) - 1, 0)
          : planned;
      return {
        id: `tl-${index}-${line}`,
        line_number: line + 1,
        sku_id: `sku-${(index * 3 + line) % SKU_COUNT}`,
        planned_qty: String(planned),
        actual_qty: actual === undefined ? undefined : String(actual),
        remaining_qty: String(planned - (actual ?? 0)),
        source_location_id: taskType === "RECEIPT" ? undefined : `loc-${(index + line) % 24}`,
        target_location_id:
          taskType === "PICK" || taskType === "CONTROL" ? undefined : `loc-${(index + line + 7) % 24}`,
        status: cancelled ? "CANCELLED" : short ? "SHORT" : "CONFIRMED",
        discrepancy_reason: short ? "Katakda kam chiqdi" : undefined,
        confirmed_at: cancelled ? undefined : isoAgo(finishedAgo + 1),
        confirmed_by: cancelled ? undefined : assignee,
        version: 2,
      };
    });

    // Bekor qilingan satr reja va faktga kirmaydi — server bilan bir xil.
    const active = lines.filter((line) => line.status !== "CANCELLED");
    const planned = active.reduce((sum, line) => sum + Number(line.planned_qty), 0);
    const actual = active.reduce((sum, line) => sum + Number(line.actual_qty ?? 0), 0);
    const shortLines = lines.filter((line) => line.status === "SHORT").length;
    const finishedAt = isoAgo(finishedAgo);
    const documented = index % 4 !== 3;

    return {
      // UUID shaklida: tarix ekrani hujjatsiz topshiriqni ID ning
      // OXIRI bilan ko'rsatadi.
      id: `0199a2f4-7c1e-7${String(index).padStart(3, "0")}-8a3b-${String(4100000 + index * 7919).padStart(12, "0")}`,
      task_type: taskType,
      status: cancelled ? "CANCELLED" : "COMPLETED",
      priority: 50,
      warehouse_code: "A",
      required_role: HISTORY_ROLE[taskType]!,
      assignee_id: assignee,
      source_process: taskType === "RECEIPT" || taskType === "PUTAWAY" ? "INBOUND" : "PICKING",
      document_number: documented
        ? taskType === "RECEIPT" || taskType === "PUTAWAY"
          ? `ПН-${String(1200 + index).padStart(6, "0")}`
          : `ЗК-${String(3400 + index).padStart(6, "0")}`
        : undefined,
      reason: cancelled ? "Buyurtma 1C da bekor qilindi" : undefined,
      offline_steps: [] as string[],
      has_shortage: shortLines > 0,
      accepted_at: started ? isoAgo(finishedAgo + workMinutes + 2) : undefined,
      started_at: started ? isoAgo(finishedAgo + workMinutes) : undefined,
      completed_at: cancelled ? undefined : finishedAt,
      cancelled_at: cancelled ? finishedAt : undefined,
      finished_at: finishedAt,
      line_count: lines.length,
      planned_qty: String(planned),
      actual_qty: String(actual),
      short_lines: shortLines,
      mismatch_lines: active.filter(
        (line) => line.actual_qty !== undefined && line.actual_qty !== line.planned_qty,
      ).length,
      version: 6,
      created_at: isoAgo(createdAgo),
      updated_at: finishedAt,
      lines,
    };
  });
}

/** Topshiriq holat tarixi: kim, qachon, qaysi holatga o'tkazdi. */
export function taskStatusHistory(task: ReturnType<typeof finishedTasks>[number]) {
  // `usr-7` — dispetcher: rezerv va bekor qilish ofisdan.
  const steps: Array<{
    from_status?: string; to_status: string; actor_id?: string; at?: string; reason?: string;
  }> = [
    { to_status: "CREATED", at: task.created_at },
    { from_status: "CREATED", to_status: "RESERVED", actor_id: "usr-7", at: task.created_at },
  ];
  if (task.started_at) {
    steps.push(
      { from_status: "RESERVED", to_status: "ACCEPTED", actor_id: task.assignee_id, at: task.accepted_at },
      { from_status: "ACCEPTED", to_status: "IN_PROGRESS", actor_id: task.assignee_id, at: task.started_at },
    );
  }
  steps.push(
    task.status === "CANCELLED"
      ? { from_status: task.started_at ? "IN_PROGRESS" : "RESERVED", to_status: "CANCELLED",
          actor_id: "usr-7", at: task.cancelled_at, reason: task.reason }
      : { from_status: "IN_PROGRESS", to_status: "COMPLETED", actor_id: task.assignee_id, at: task.completed_at },
  );
  return steps.map((step) => ({
    from_status: step.from_status,
    to_status: step.to_status,
    reason: step.reason,
    actor_id: step.actor_id,
    device_id: step.actor_id && step.actor_id !== "usr-7" ? "TSD-003" : undefined,
    occurred_at: step.at ?? task.created_at,
  }));
}
